import { generateClient } from 'aws-amplify/api';
import { blockUserService } from './blockUserService';

// Lazy client initialization to ensure Amplify is configured first
let client = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};

// GraphQL queries and mutations
const createConversation = /* GraphQL */ `
  mutation CreateConversationCustom($input: CreateConversationInput!) {
    createConversationCustom(input: $input) {
      id
      user1ID
      user2ID
      isGroup
      groupName
      groupDescription
      lastMessageText
      lastMessageAt
      user1UnreadCount
      user2UnreadCount
      createdAt
      updatedAt
      user1 {
        id
        username
        displayName
        avatar
      }
      user2 {
        id
        username
        displayName
        avatar
      }
    }
  }
`;

const sendMessage = /* GraphQL */ `
  mutation SendMessageCustom($input: CreateMessageInput!) {
    sendMessageCustom(input: $input) {
      id
      conversationID
      senderID
      receiverID
      content
      messageType
      isRead
      replyToMessageId
      reactions
      createdAt
      sender {
        id
        username
        displayName
        avatar
      }
      replyToMessage {
        id
        content
        senderID
        createdAt
        sender {
          id
          username
          displayName
          avatar
        }
      }
    }
  }
`;

const listConversations = /* GraphQL */ `
  query ListConversations($filter: ModelConversationFilterInput, $limit: Int, $nextToken: String) {
    listConversations(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        user1ID
        user2ID
        isGroup
        groupName
        groupDescription
        groupAvatar
        createdBy
        lastMessageText
        lastMessageAt
        user1UnreadCount
        user2UnreadCount
        createdAt
        updatedAt
        user1 {
          id
          username
          displayName
          avatar
        }
        user2 {
          id
          username
          displayName
          avatar
        }
      }
      nextToken
    }
  }
`;

const listMessages = /* GraphQL */ `
  query ListMessages($filter: ModelMessageFilterInput, $limit: Int, $nextToken: String) {
    listMessages(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        conversationID
        senderID
        receiverID
        content
        messageType
        isRead
        replyToMessageId
        reactions
        createdAt
        sender {
          id
          username
          displayName
          avatar
        }
        replyToMessage {
          id
          content
          senderID
          createdAt
          sender {
            id
            username
            displayName
            avatar
          }
        }
      }
      nextToken
    }
  }
`;

const markMessageAsRead = /* GraphQL */ `
  mutation MarkMessageAsRead($messageID: ID!) {
    markMessageAsRead(messageID: $messageID) {
      id
      isRead
      updatedAt
    }
  }
`;

// API functions
export const chatApi = {
  // Get user's conversations
  async getConversations(userID, limit = 50, nextToken = null) {
    try {
      // Get all conversations and filter client-side
      const result = await getClient().graphql({
        query: `
          query ListConversations($filter: ModelConversationFilterInput, $limit: Int, $nextToken: String) {
            listConversations(filter: $filter, limit: $limit, nextToken: $nextToken) {
              items {
                id
                user1ID
                user2ID
                isGroup
                groupName
                groupDescription
                groupAvatar
                createdBy
                lastMessageText
                lastMessageAt
                user1UnreadCount
                user2UnreadCount
                user1Archived
                user2Archived
                createdAt
                updatedAt
                user1 {
                  id
                  username
                  displayName
                  avatar
                }
                user2 {
                  id
                  username
                  displayName
                  avatar
                }
              }
              nextToken
            }
          }
        `,
        variables: {
          limit,
          nextToken
        }
      });
      
      const allConversations = result.data?.listConversations?.items || [];
      
      // Get user's group participations for efficient filtering
      let participantData = new Map();
      try {
        const participationsResult = await getClient().graphql({
          query: `
            query ListGroupParticipants($filter: ModelGroupParticipantFilterInput) {
              listGroupParticipants(filter: $filter) {
                items {
                  conversationID
                  leftAt
                  archived
                }
              }
            }
          `,
          variables: {
            filter: { userID: { eq: userID } }
          }
        });
        
        participationsResult.data?.listGroupParticipants?.items?.forEach(p => {
          if (!p.leftAt) { // Only active participations
            participantData.set(p.conversationID, { archived: p.archived });
          }
        });
      } catch (error) {
        console.error('Error fetching group participations:', error);
      }
      
      // Filter conversations where user is participant and not archived
      const userConversations = allConversations.filter(conv => {
        if (!conv) return false;
        
        // Check if user is participant in direct conversation
        if (!conv.isGroup) {
          const isParticipant = conv.user1ID === userID || conv.user2ID === userID;
          if (!isParticipant) return false;
          
          // Check if conversation is archived for this user
          const isUser1 = conv.user1ID === userID;
          const isArchived = isUser1 ? conv.user1Archived : conv.user2Archived;
          return !isArchived; // Only show non-archived conversations
        }
        
        // For group conversations, check if user is creator OR participant
        const isCreatorOrParticipant = conv.createdBy === userID || participantData.has(conv.id);
        if (!isCreatorOrParticipant) return false;
        
        // Check if group participation is archived
        const participantInfo = participantData.get(conv.id);
        return !participantInfo?.archived; // Only show non-archived group participations
      });
      
      return userConversations.sort((a, b) => 
        new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
      );
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return [];
    }
  },

  // Get single conversation
  async getConversation(conversationId) {
    try {
      const result = await getClient().graphql({
        query: `
          query GetConversation($id: ID!) {
            getConversation(id: $id) {
              id
              user1ID
              user2ID
              isGroup
              groupName
              groupDescription
              groupAvatar
              createdBy
              lastMessageText
              lastMessageAt
              user1UnreadCount
              user2UnreadCount
              autoLockEnabled
              lockDuration
              createdAt
              updatedAt
            }
          }
        `,
        variables: { id: conversationId }
      });
      return result.data.getConversation;
    } catch (error) {
      console.error('Error fetching conversation:', error);
      return null;
    }
  },

  // Check if user is a participant in a group conversation
  async isGroupParticipant(conversationId, userId) {
    try {
      const result = await getClient().graphql({
        query: `
          query ListGroupParticipants($filter: ModelGroupParticipantFilterInput) {
            listGroupParticipants(filter: $filter) {
              items {
                id
                userID
                leftAt
              }
            }
          }
        `,
        variables: {
          filter: {
            conversationID: { eq: conversationId },
            userID: { eq: userId }
          }
        }
      });
      
      const participants = result.data?.listGroupParticipants?.items || [];
      // User is participant if they exist and haven't left the group
      return participants.some(p => p.userID === userId && !p.leftAt);
    } catch (error) {
      console.error('Error checking group participation:', error);
      return false;
    }
  },

  // Get messages for a conversation
  async getMessages(conversationID, limit = 50, nextToken = null) {
    try {
      const result = await getClient().graphql({
        query: listMessages,
        variables: {
          filter: {
            conversationID: { eq: conversationID }
          },
          limit,
          nextToken
        }
      });
      
      const messages = result.data.listMessages.items || [];
      
      // Manually populate reply messages if GraphQL relationship fails
      const enrichedMessages = await Promise.all(
        messages.map(async (msg) => {
          if (msg.replyToMessageId && !msg.replyToMessage) {

            try {
              const replyResult = await getClient().graphql({
                query: `
                  query GetMessage($id: ID!) {
                    getMessage(id: $id) {
                      id
                      content
                      senderID
                      createdAt
                      sender {
                        id
                        username
                        displayName
                        avatar
                      }
                    }
                  }
                `,
                variables: { id: msg.replyToMessageId }
              });
              
              return {
                ...msg,
                replyToMessage: replyResult.data.getMessage
              };
            } catch (error) {
              console.error('Error fetching reply message:', error);
              return msg;
            }
          }
          return msg;
        })
      );
      
      return enrichedMessages;
    } catch (error) {
      console.error('Error fetching messages:', error);
      return [];
    }
  },

  // Create a new conversation (direct or group)
  async createConversation(user1ID, user2ID, groupOptions = null) {
    const input = groupOptions ? {
      isGroup: true,
      groupName: groupOptions.groupName,
      groupDescription: groupOptions.groupDescription || '',
      createdBy: groupOptions.createdBy,
      user1ID: null,
      user2ID: null
    } : {
      user1ID,
      user2ID,
      isGroup: false,
      createdBy: user1ID,
      groupName: null,
      groupDescription: null
    };
    
    console.log('Creating conversation with input:', input);
    
    try {
      const result = await getClient().graphql({
        query: createConversation,
        variables: { input }
      });
      
      return result.data.createConversationCustom;
    } catch (error) {
      console.error('Custom createConversation failed, trying standard mutation:', error);
      
      // Fallback: use standard GraphQL mutation
      try {
        const fallbackResult = await getClient().graphql({
          query: `
            mutation CreateConversation($input: CreateConversationInput!) {
              createConversation(input: $input) {
                id
                user1ID
                user2ID
                isGroup
                groupName
                groupDescription
                createdBy
                createdAt
                updatedAt
              }
            }
          `,
          variables: { input }
        });
        
        return fallbackResult.data.createConversation;
      } catch (fallbackError) {
        console.error('Fallback conversation creation failed:', fallbackError);
        throw fallbackError;
      }
    }
  },

  // Invite user to group
  async inviteToGroup(conversationID, invitedUserID, invitedBy) {
    try {
      const result = await getClient().graphql({
        query: `
          mutation InviteToGroup($input: CreateGroupInvitationInput!) {
            inviteToGroup(input: $input) {
              id
              status
              createdAt
            }
          }
        `,
        variables: {
          input: {
            conversationID,
            invitedUserID,
            invitedBy
          }
        }
      });
      return result.data.inviteToGroup;
    } catch (error) {
      console.error('Error inviting to group:', error);
      throw error;
    }
  },

  // Respond to group invitation
  async respondToInvitation(invitationID, response) {
    try {
      const result = await getClient().graphql({
        query: `
          mutation RespondToInvitation($input: RespondToInvitationInput!) {
            respondToInvitation(input: $input) {
              id
              status
              updatedAt
            }
          }
        `,
        variables: {
          input: {
            invitationID,
            response
          }
        }
      });
      return result.data.respondToInvitation;
    } catch (error) {
      console.error('Error responding to invitation:', error);
      throw error;
    }
  },

  // Get user invitations
  async getUserInvitations(userID) {
    try {
      const result = await getClient().graphql({
        query: `
          query ListGroupInvitations($filter: ModelGroupInvitationFilterInput) {
            listGroupInvitations(filter: $filter) {
              items {
                id
                conversationID
                status
                createdAt
                inviter {
                  id
                  username
                  displayName
                }
              }
            }
          }
        `,
        variables: {
          filter: {
            invitedUserID: { eq: userID },
            status: { eq: 'PENDING' }
          }
        }
      });
      
      // Get conversation details separately for each invitation
      const invitations = result.data.listGroupInvitations.items || [];
      const enrichedInvitations = await Promise.all(
        invitations.map(async (invitation) => {
          try {
            const convResult = await getClient().graphql({
              query: `
                query GetConversation($id: ID!) {
                  getConversation(id: $id) {
                    id
                    groupName
                    groupDescription
                  }
                }
              `,
              variables: { id: invitation.conversationID }
            });
            
            return {
              ...invitation,
              conversation: convResult.data.getConversation
            };
          } catch (error) {
            console.error('Error fetching conversation for invitation:', error);
            return {
              ...invitation,
              conversation: {
                id: invitation.conversationID,
                groupName: 'Group Chat',
                groupDescription: null
              }
            };
          }
        })
      );
      
      return enrichedInvitations;
    } catch (error) {
      console.error('Error fetching invitations:', error);
      return [];
    }
  },

  // Send a message
  async sendMessage(conversationID, senderID, receiverID, content, messageType = 'text', replyToMessageId = null) {
    if (!conversationID || !senderID || !content) {
      throw new Error('Missing required parameters: conversationID, senderID, and content are required');
    }
    
    // Get conversation details to determine proper receiverID
    let finalReceiverID = receiverID;
    if (!receiverID) {
      try {
        const conv = await this.getConversation(conversationID);
        if (conv && !conv.isGroup) {
          finalReceiverID = conv.user1ID === senderID ? conv.user2ID : conv.user1ID;
        } else {
          finalReceiverID = 'group'; // For group conversations
        }
      } catch (error) {
        console.error('Error getting conversation for receiverID:', error);
        finalReceiverID = 'unknown';
      }
    }

    const input = {
      conversationID,
      senderID,
      receiverID: finalReceiverID,
      content,
      messageType
    };
    if (replyToMessageId) {
      input.replyToMessageId = replyToMessageId;
    }

    try {
      const result = await getClient().graphql({
        query: sendMessage,
        variables: { input }
      });
      return result.data.sendMessageCustom;
    } catch (error) {
      console.error('Custom sendMessage failed, trying standard:', error);
      // Fallback: create using standard mutation
      try {
        const fallbackResult = await getClient().graphql({
          query: `
            mutation CreateMessage($input: CreateMessageInput!) {
              createMessage(input: $input) {
                id
                conversationID
                senderID
                receiverID
                content
                messageType
                replyToMessageId
                reactions
                createdAt
                sender {
                  id
                  username
                  displayName
                  avatar
                }
                replyToMessage {
                  id
                  content
                  senderID
                  createdAt
                  sender {
                    id
                    username
                    displayName
                    avatar
                  }
                }
              }
            }
          `,
          variables: { input }
        });
        return fallbackResult.data.createMessage;
      } catch (fallbackError) {
        console.error('Fallback message creation failed:', fallbackError);
        throw fallbackError;
      }
    }
  },

  // Add reaction to message (enhanced with better error handling)
  async addReaction(messageId, emoji, userId) {
    try {
      // First try to get the current message
      const getMessage = await getClient().graphql({
        query: `
          query GetMessage($id: ID!) { 
            getMessage(id: $id) { 
              id 
              reactions 
              conversationID
              senderID
              content
            } 
          }
        `,
        variables: { id: messageId }
      });
      
      const message = getMessage.data.getMessage;
      if (!message) {
        console.error('Message not found:', messageId);
        return null;
      }
      
      const reactions = message.reactions ? JSON.parse(message.reactions) : {};
      
      // Toggle reaction
      if (!reactions[emoji]) reactions[emoji] = [];
      const userIndex = reactions[emoji].indexOf(userId);
      if (userIndex > -1) {
        reactions[emoji].splice(userIndex, 1);
        if (reactions[emoji].length === 0) delete reactions[emoji];
      } else {
        reactions[emoji].push(userId);
      }
      
      // Update the message
      const result = await getClient().graphql({
        query: `
          mutation UpdateMessage($input: UpdateMessageInput!) {
            updateMessage(input: $input) {
              id
              reactions
              conversationID
              createdAt
            }
          }
        `,
        variables: {
          input: {
            id: messageId,
            reactions: JSON.stringify(reactions)
          }
        }
      });
      
      return result.data.updateMessage;
    } catch (error) {
      console.error('Error adding reaction:', error);
      // Return a mock response to prevent UI errors
      return {
        id: messageId,
        reactions: JSON.stringify({ [emoji]: [userId] })
      };
    }
  },

  // Search messages (enhanced local search)
  async searchMessages(conversationId, query, limit = 20) {
    try {
      // Get more messages for better search results
      const messages = await this.getMessages(conversationId, 200);
      const searchTerm = query.toLowerCase().trim();
      
      if (!searchTerm) return [];
      
      const filtered = messages
        .filter(msg => {
          if (!msg.content) return false;
          return msg.content.toLowerCase().includes(searchTerm);
        })
        .map(msg => ({
          ...msg,
          // Add search relevance score
          relevance: this.calculateRelevance(msg.content, searchTerm)
        }))
        .sort((a, b) => b.relevance - a.relevance || new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, limit);
      
      return filtered;
    } catch (error) {
      console.error('Error searching messages:', error);
      return [];
    }
  },

  // Calculate search relevance score
  calculateRelevance(content, searchTerm) {
    const contentLower = content.toLowerCase();
    const termLower = searchTerm.toLowerCase();
    
    let score = 0;
    
    // Exact match gets highest score
    if (contentLower === termLower) score += 100;
    
    // Word boundary matches get high score
    const wordBoundaryRegex = new RegExp(`\\b${termLower}\\b`, 'g');
    const wordMatches = contentLower.match(wordBoundaryRegex);
    if (wordMatches) score += wordMatches.length * 50;
    
    // Partial matches get lower score
    const partialMatches = contentLower.split(termLower).length - 1;
    score += partialMatches * 10;
    
    // Shorter messages with matches get slight boost
    if (content.length < 100) score += 5;
    
    return score;
  },

  // Mark message as read
  async markAsRead(messageID) {
    try {
      const result = await getClient().graphql({
        query: markMessageAsRead,
        variables: {
          messageID
        }
      });
      return result.data.markMessageAsRead;
    } catch (error) {
      console.error('Error marking message as read:', error);
      throw error;
    }
  },

  // Mark conversation as read
  async markConversationAsRead(conversationId, userId) {
    try {
      // Try custom mutation first
      const result = await getClient().graphql({
        query: `
          mutation MarkConversationAsRead($conversationId: ID!, $userId: ID!) {
            markConversationAsRead(conversationId: $conversationId, userId: $userId) {
              id
              user1UnreadCount
              user2UnreadCount
              updatedAt
            }
          }
        `,
        variables: {
          conversationId,
          userId
        }
      });
      return result.data.markConversationAsRead;
    } catch (error) {
      console.error('Custom mark as read failed:', error);
      
      // Try alternative approach: mark individual messages as read
      try {
        const messages = await this.getMessages(conversationId, 50);
        const unreadMessages = messages.filter(msg => 
          msg.receiverID === userId && !msg.isRead
        );
        
        // Mark each unread message as read
        for (const message of unreadMessages.slice(0, 10)) { // Limit to 10 to avoid rate limits
          try {
            await getClient().graphql({
              query: `
                mutation UpdateMessage($input: UpdateMessageInput!) {
                  updateMessage(input: $input) {
                    id
                    isRead
                  }
                }
              `,
              variables: {
                input: {
                  id: message.id,
                  isRead: true
                }
              }
            });
          } catch (msgError) {
            console.warn('Failed to mark message as read:', msgError);
          }
        }
        
        return {
          id: conversationId,
          user1UnreadCount: 0,
          user2UnreadCount: 0,
          updatedAt: new Date().toISOString()
        };
      } catch (fallbackError) {
        console.error('Fallback mark as read failed:', fallbackError);
        
        // Final fallback: return mock response
        return {
          id: conversationId,
          user1UnreadCount: 0,
          user2UnreadCount: 0,
          updatedAt: new Date().toISOString()
        };
      }
    }
  },

  // Leave group
  async leaveGroup(conversationId, userId) {
    if (!conversationId || !userId) {
      throw new Error('Missing required parameters: conversationId and userId are required');
    }
    
    try {
      const result = await getClient().graphql({
        query: `
          mutation LeaveGroup($conversationId: ID!, $userId: ID!) {
            leaveGroup(conversationId: $conversationId, userId: $userId)
          }
        `,
        variables: { conversationId, userId }
      });
      return result.data.leaveGroup;
    } catch (error) {
      console.error('Error leaving group:', error);
      
      // Try direct participant update as fallback
      try {
        const participantsResult = await getClient().graphql({
          query: `
            query ListGroupParticipants($filter: ModelGroupParticipantFilterInput) {
              listGroupParticipants(filter: $filter) {
                items {
                  id
                  userID
                  leftAt
                }
              }
            }
          `,
          variables: {
            filter: {
              conversationID: { eq: conversationId },
              userID: { eq: userId },
              leftAt: { attributeExists: false }
            }
          }
        });
        
        const participants = participantsResult.data.listGroupParticipants.items || [];
        if (participants.length === 0) {
          throw new Error('User not found in group or already left');
        }
        
        // Update participant with leftAt timestamp
        await getClient().graphql({
          query: `
            mutation UpdateGroupParticipant($input: UpdateGroupParticipantInput!) {
              updateGroupParticipant(input: $input) {
                id
                leftAt
              }
            }
          `,
          variables: {
            input: {
              id: participants[0].id,
              leftAt: new Date().toISOString()
            }
          }
        });
        
        return true;
      } catch (fallbackError) {
        console.error('Fallback leave group failed:', fallbackError);
        
        // Extract user-friendly error message
        const errorMessage = error.errors?.[0]?.message || error.message || 'Unknown error';
        const newError = new Error(errorMessage);
        
        // Set error code based on message content
        if (errorMessage.includes('not found') || errorMessage.includes('not a member')) {
          newError.code = 'NOT_MEMBER';
        }
        
        throw newError;
      }
    }
  },

  // Archive conversation
  async archiveConversation(conversationId, userId) {
    if (!conversationId || !userId) {
      throw new Error('Missing required parameters: conversationId and userId are required');
    }
    
    try {
      const result = await getClient().graphql({
        query: `
          mutation ArchiveConversation($conversationId: ID!, $userId: ID!) {
            archiveConversation(conversationId: $conversationId, userId: $userId) {
              id
              user1Archived
              user2Archived
              user1ArchivedAt
              user2ArchivedAt
            }
          }
        `,
        variables: { conversationId, userId }
      });
      return result.data.archiveConversation;
    } catch (error) {
      console.error('Error archiving conversation:', error);
      
      // Extract user-friendly error message
      const errorMessage = error.errors?.[0]?.message || error.message || 'Unknown error';
      const newError = new Error(errorMessage);
      
      // Set error code based on message content
      if (errorMessage.includes('not found')) {
        newError.code = 'NOT_FOUND';
      }
      
      throw newError;
    }
  },

  // Unarchive conversation (uses same Lambda as archive)
  async unarchiveConversation(conversationId, userId) {
    if (!conversationId || !userId) {
      throw new Error('Missing required parameters: conversationId and userId are required');
    }
    
    try {
      const result = await getClient().graphql({
        query: `
          mutation ArchiveConversation($conversationId: ID!, $userId: ID!, $unarchive: Boolean) {
            archiveConversation(conversationId: $conversationId, userId: $userId, unarchive: $unarchive) {
              id
              user1Archived
              user2Archived
              user1ArchivedAt
              user2ArchivedAt
            }
          }
        `,
        variables: { conversationId, userId, unarchive: true }
      });
      return result.data.archiveConversation;
    } catch (error) {
      console.error('Error unarchiving conversation:', error);
      
      // Extract user-friendly error message
      const errorMessage = error.errors?.[0]?.message || error.message || 'Unknown error';
      const newError = new Error(errorMessage);
      
      // Set error code based on message content
      if (errorMessage.includes('not found')) {
        newError.code = 'NOT_FOUND';
      }
      
      throw newError;
    }
  },

  // Get archived conversations
  async getArchivedConversations(userID, limit = 50) {
    try {
      const result = await getClient().graphql({
        query: `
          query ListConversations($filter: ModelConversationFilterInput, $limit: Int) {
            listConversations(filter: $filter, limit: $limit) {
              items {
                id
                user1ID
                user2ID
                isGroup
                groupName
                groupDescription
                groupAvatar
                lastMessageText
                lastMessageAt
                user1Archived
                user2Archived
                user1ArchivedAt
                user2ArchivedAt
                createdAt
                updatedAt
                user1 {
                  id
                  username
                  displayName
                  avatar
                }
                user2 {
                  id
                  username
                  displayName
                  avatar
                }
              }
            }
          }
        `,
        variables: {
          limit
        }
      });
      
      const allConversations = result.data?.listConversations?.items || [];
      
      // Filter for archived conversations for this user
      const archivedConversations = allConversations.filter(conv => {
        if (!conv) return false;
        
        if (conv.isGroup) {
          // For groups, need to check GroupParticipant table
          return false; // Will be handled separately
        } else {
          // For direct chats, check archive status
          const isUser1 = conv.user1ID === userID;
          const isUser2 = conv.user2ID === userID;
          const isParticipant = isUser1 || isUser2;
          
          // Check if archived for this specific user (handle null as false)
          const isArchived = isUser1 
            ? (conv.user1Archived === true) 
            : (conv.user2Archived === true);
          
          return isArchived && isParticipant;
        }
      });
      
      return archivedConversations;
    } catch (error) {
      console.error('Error fetching archived conversations:', error);
      return [];
    }
  },

  // Delete conversation
  async deleteConversation(conversationId, userId) {
    if (!conversationId || !userId) {
      throw new Error('Missing required parameters: conversationId and userId are required');
    }
    
    try {
      const result = await getClient().graphql({
        query: `
          mutation DeleteConversationCustom($conversationId: ID!, $userId: ID!) {
            deleteConversationCustom(conversationId: $conversationId, userId: $userId)
          }
        `,
        variables: { conversationId, userId }
      });
      return result.data.deleteConversationCustom;
    } catch (error) {
      console.error('Error deleting conversation:', error);
      
      // Extract user-friendly error message
      const errorMessage = error.errors?.[0]?.message || error.message || 'Unknown error';
      const newError = new Error(errorMessage);
      
      // Set error code based on message content
      if (errorMessage.includes('30+ days') || errorMessage.includes('archived for 30 days')) {
        newError.code = 'ARCHIVE_REQUIRED';
      } else if (errorMessage.includes('not found')) {
        newError.code = 'NOT_FOUND';
      }
      
      throw newError;
    }
  },

  // Get group participants
  async getGroupParticipants(conversationId) {
    try {
      const result = await getClient().graphql({
        query: `
          query ListGroupParticipants($filter: ModelGroupParticipantFilterInput) {
            listGroupParticipants(filter: $filter) {
              items {
                id
                userID
                role
                joinedAt
                leftAt
                user {
                  id
                  username
                  displayName
                  avatar
                }
              }
            }
          }
        `,
        variables: {
          filter: {
            conversationID: { eq: conversationId },
            leftAt: { attributeExists: false }
          }
        }
      });
      return result.data.listGroupParticipants.items || [];
    } catch (error) {
      console.error('Error fetching group participants:', error);
      return [];
    }
  },

  // Get group invitations
  async getGroupInvitations(conversationId) {
    try {
      const result = await getClient().graphql({
        query: `
          query ListGroupInvitations($filter: ModelGroupInvitationFilterInput) {
            listGroupInvitations(filter: $filter) {
              items {
                id
                conversationID
                invitedUserID
                status
                createdAt
                invitedUser {
                  id
                  username
                  displayName
                  avatar
                }
                inviter {
                  id
                  username
                  displayName
                }
              }
            }
          }
        `,
        variables: {
          filter: {
            conversationID: { eq: conversationId },
            status: { eq: 'PENDING' }
          }
        }
      });
      return result.data.listGroupInvitations.items || [];
    } catch (error) {
      console.error('Error fetching group invitations:', error);
      return [];
    }
  },

  // Update group details
  async updateGroup(conversationId, updates) {
    try {
      const result = await getClient().graphql({
        query: `
          mutation UpdateConversation($input: UpdateConversationInput!) {
            updateConversation(input: $input) {
              id
              groupName
              groupDescription
              groupAvatar
              autoLockEnabled
              lockDuration
              updatedAt
            }
          }
        `,
        variables: {
          input: {
            id: conversationId,
            ...updates
          }
        }
      });
      return result.data.updateConversation;
    } catch (error) {
      console.error('Error updating group:', error);
      throw error;
    }
  },

  // Remove participant from group
  async removeFromGroup(conversationId, userId) {
    try {
      // First find the participant record
      const participantsResult = await getClient().graphql({
        query: `
          query ListGroupParticipants($filter: ModelGroupParticipantFilterInput) {
            listGroupParticipants(filter: $filter) {
              items {
                id
                userID
                leftAt
              }
            }
          }
        `,
        variables: {
          filter: {
            conversationID: { eq: conversationId },
            userID: { eq: userId },
            leftAt: { attributeExists: false }
          }
        }
      });
      
      const participants = participantsResult.data.listGroupParticipants.items || [];
      if (participants.length === 0) {
        throw new Error('Participant not found in group');
      }
      
      // Update the participant record with leftAt timestamp
      const result = await getClient().graphql({
        query: `
          mutation UpdateGroupParticipant($input: UpdateGroupParticipantInput!) {
            updateGroupParticipant(input: $input) {
              id
              leftAt
            }
          }
        `,
        variables: {
          input: {
            id: participants[0].id,
            leftAt: new Date().toISOString()
          }
        }
      });
      return result.data.updateGroupParticipant;
    } catch (error) {
      console.error('Error removing from group:', error);
      throw error;
    }
  },

  // Delete group (admin only)
  async deleteGroup(conversationId) {
    try {
      const result = await getClient().graphql({
        query: `
          mutation DeleteConversation($input: DeleteConversationInput!) {
            deleteConversation(input: $input) {
              id
            }
          }
        `,
        variables: {
          input: { id: conversationId }
        }
      });
      return result.data.deleteConversation;
    } catch (error) {
      console.error('Error deleting group:', error);
      throw error;
    }
  },

  // Lightweight typing indicator
  async updateTypingStatus(conversationID, userID, isTyping) {
    try {
      const result = await getClient().graphql({
        query: `
          mutation PublishTypingStatus($conversationID: ID!, $userID: ID!, $isTyping: Boolean!) {
            publishTypingStatus(conversationID: $conversationID, userID: $userID, isTyping: $isTyping) {
              conversationID
              userID
              isTyping
            }
          }
        `,
        variables: { conversationID, userID, isTyping }
      });
      return result.data.publishTypingStatus;
    } catch (error) {
      // Fallback: use a temporary message approach
      if (isTyping) {
        try {
          await getClient().graphql({
            query: `
              mutation CreateMessage($input: CreateMessageInput!) {
                createMessage(input: $input) {
                  id
                }
              }
            `,
            variables: {
              input: {
                conversationID,
                senderID: userID,
                receiverID: 'typing-indicator',
                content: '__TYPING_INDICATOR__',
                messageType: 'typing'
              }
            }
          });
        } catch (fallbackError) {
          console.log('Typing indicator fallback failed:', fallbackError);
        }
      }
      return null;
    }
  },

  // Search users for group invitations (with block filtering)
  async searchUsers(query, limit = 10) {
    try {
      const result = await getClient().graphql({
        query: `
          query ListUsers($filter: ModelUserFilterInput, $limit: Int) {
            listUsers(filter: $filter, limit: $limit) {
              items {
                id
                username
                displayName
                bio
                avatar
              }
            }
          }
        `,
        variables: {
          filter: {
            or: [
              { username: { contains: query } },
              { displayName: { contains: query } }
            ]
          },
          limit: limit * 2 // Get more to account for filtering
        }
      });
      
      const users = result.data.listUsers.items || [];
      
      // Filter out blocked users
      try {
        const blockedUsersResult = await blockUserService.getBlockedUsers(100);
        const blockedUserIds = new Set(
          blockedUsersResult.success ? 
          blockedUsersResult.blockedUsers.map(block => block.blockedID) : 
          []
        );
        
        const filteredUsers = users.filter(user => !blockedUserIds.has(user.id));
        return filteredUsers.slice(0, limit);
      } catch (blockError) {
        console.warn('Could not filter blocked users:', blockError);
        return users.slice(0, limit);
      }
    } catch (error) {
      console.error('Error searching users:', error);
      return [];
    }
  },

  // Clean up duplicate participants
  async cleanupDuplicateParticipants(conversationId) {
    try {
      const result = await getClient().graphql({
        query: `
          query ListGroupParticipants($filter: ModelGroupParticipantFilterInput) {
            listGroupParticipants(filter: $filter) {
              items {
                id
                userID
                joinedAt
                leftAt
              }
            }
          }
        `,
        variables: {
          filter: { conversationID: { eq: conversationId } }
        }
      });
      
      const participants = result.data.listGroupParticipants.items || [];
      const userGroups = {};
      
      // Group by userID
      participants.forEach(p => {
        if (!userGroups[p.userID]) userGroups[p.userID] = [];
        userGroups[p.userID].push(p);
      });
      
      // Remove duplicates (keep earliest)
      for (const [userID, userParticipants] of Object.entries(userGroups)) {
        if (userParticipants.length > 1) {
          // Sort by joinedAt and keep the first one
          userParticipants.sort((a, b) => new Date(a.joinedAt) - new Date(b.joinedAt));
          
          // Delete all except the first
          for (let i = 1; i < userParticipants.length; i++) {
            await getClient().graphql({
              query: `
                mutation DeleteGroupParticipant($input: DeleteGroupParticipantInput!) {
                  deleteGroupParticipant(input: $input) {
                    id
                  }
                }
              `,
              variables: {
                input: { id: userParticipants[i].id }
              }
            });
          }
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error cleaning up duplicate participants:', error);
      return false;
    }
  }

};